import { useState, useCallback, useRef, useEffect } from 'react';
import apiClient from '../services/api/client';

/**
 * Custom hook for API calls with automatic loading and error states
 * @returns {Object} API methods and state
 */
export const useApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  // Cancel pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Execute an API request
   */
  const execute = useCallback(async (requestFn, options = {}) => {
    const { onSuccess, onError, showLoading = true } = options;

    try {
      // Create abort controller for request cancellation
      abortControllerRef.current = new AbortController();
      
      if (showLoading) {
        setIsLoading(true);
      }
      setError(null);

      const response = await requestFn(abortControllerRef.current.signal);
      
      if (onSuccess) {
        onSuccess(response);
      }
      
      return response;
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        // Request was cancelled, don't set error
        return null;
      }
      
      setError(err);
      
      if (onError) {
        onError(err);
      }
      
      throw err;
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * GET request
   */
  const get = useCallback((url, params = {}, options = {}) => {
    return execute(
      (signal) => apiClient.get(url, { params, signal }),
      options
    );
  }, [execute]);

  /**
   * POST request
   */
  const post = useCallback((url, data = {}, options = {}) => {
    return execute(
      (signal) => apiClient.post(url, data, { signal }),
      options
    );
  }, [execute]);

  /**
   * PATCH request
   */
  const patch = useCallback((url, data = {}, options = {}) => {
    return execute(
      (signal) => apiClient.patch(url, data, { signal }),
      options
    );
  }, [execute]);

  /**
   * DELETE request
   */
  const del = useCallback((url, options = {}) => {
    return execute(
      (signal) => apiClient.delete(url, { signal }),
      options
    );
  }, [execute]);

  /**
   * POST with FormData (for file uploads)
   */
  const postForm = useCallback((url, formData, options = {}) => {
    return execute(
      (signal) => apiClient.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal
      }),
      options
    );
  }, [execute]);

  return {
    isLoading,
    error,
    get,
    post,
    patch,
    delete: del,
    postForm,
  };
};
